<i18n>
    [
        "product__prepurchase--buyNowCta",
        "product__noSizeSelected--label",
        "product__prepurchase--oosErrorLabel",
        "product__prepurchase--subscriptionStatusErrorLabel",
        "product__prepurchase--nonSubscriberBuyNowCta",
        "product__prepurchase--pausedUserBuyNowCta",
        "product__prepurchase--cancelledUserBuyNowCta",
    ]
</i18n>
<template>
    <div class="c-pre-purchase-buy-button">
        <span v-if="hasError" class="o-flex-vertical-center u-spacer--1 u-text--color-primary">
            <BaseIcon
                icon="global--alert"
                size="12px"
                color="primary"
                class="c-pre-purchase-buy-button__alert-icon"
            />
            <BaseMarkdown
                :source="prePurchaseError"
                class="o-text--caption"
            />
        </span>
        <LoadingButton
            :isLoading="purchaseToBoxIsLoading"
            @click="handleClick"
        >
            <SuccessCheck
                ref="successCheckRef"
                size="16px"
                :text="t('product__addToBoxSuccess--CTA')"
            >
                <template #action>
                    {{ buttonText }}
                </template>
            </SuccessCheck>
        </LoadingButton>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';
import { useActions, useState, useGetters } from '~coreModules/core/js/composables/vuexHelpers';
import { getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';
import { PREVIEW_PRE_PURCHASE } from '~routes/product/js/product-store';

import { MODALS } from '~modules/modals/js/modal-constants';
import { rentalRouteNames, reviewRouteObjects } from '~modules/core/js/router-constants';
import { SUBSCRIPTION_STATUSES } from '~modules/profile/js/profile-constants';

import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';

import LoadingButton from '~coreModules/core/components/ui/buttons/LoadingButton.vue';
import SuccessCheck from '~coreModules/core/components/ui/SuccessCheck.vue';
import BaseMarkdown from '~coreModules/core/components/ui/BaseMarkdown.vue';

const purchaseToBoxIsLoading = ref(false);
const prePurchaseError = ref('');

const route = useRoute();
const router = useRouter();
const r15Svc = useR15Svc();
const logger = useLogger();

const { t } = useI18n();

const productModuleName = computed(() => getProductModuleNameFromRoute(route));

const { previewPrePurchase } = useActions(productModuleName.value, { previewPrePurchase: PREVIEW_PRE_PURCHASE });

const noSizeSelectedErrorText = computed(() => t('product__noSizeSelected--label'));
const nonSubscribedErrorText = computed(() => t('product__prepurchase--subscriptionStatusErrorLabel'));
const { selectedSku } = useState(productModuleName.value, {
    selectedSku: state => state.selectedSku,
});

const {
    effectiveSubscriptionStatus,
    isPausedOrPauseRequested,
    isCancelledOrCancelRequested,
} = useGetters(PROFILE_MODULE_NAME, [
    'effectiveSubscriptionStatus',
    'isPausedOrPauseRequested',
    'isCancelledOrCancelRequested',
]);

const isSkuSelected = computed(() => selectedSku.value !== '');

function aboveBuyButtonText() {
    if (!isSkuSelected.value) {
        prePurchaseError.value = noSizeSelectedErrorText.value;
        return;
    }

    if (isPausedOrPauseRequested.value || isCancelledOrCancelRequested.value) {
        prePurchaseError.value = nonSubscribedErrorText.value;
        return;
    }

    prePurchaseError.value = '';
}

async function clickedPurchaseToBox() {
    aboveBuyButtonText();
    if (prePurchaseError.value !== '') return;
    purchaseToBoxIsLoading.value = true;

    try {
        await previewPrePurchase({ r15Svc, skuId: selectedSku.value });

        router.push({
            path: route.path,
            query: {
                ...route.query,
                modalSkuId: selectedSku.value,
            },
            hash: `#${MODALS.prePurchaseCheckout.id}`,
        });
    } catch (error) {
        prePurchaseError.value = error.data?.message ?? '';
        logger.debugError('Failed to call preview endpoint for prepurchase', error);
    }

    purchaseToBoxIsLoading.value = false;
}

const hasError = computed(() => prePurchaseError.value !== '');

const buttonText = computed(() => {
    const slugMap = new Map([
        [SUBSCRIPTION_STATUSES.nonSubscriber, t('product__prepurchase--nonSubscriberBuyNowCta')],
        [SUBSCRIPTION_STATUSES.paused, t('product__prepurchase--pausedUserBuyNowCta')],
        [SUBSCRIPTION_STATUSES.cancelled, t('product__prepurchase--cancelledUserBuyNowCta')],
    ]);
    return slugMap.get(effectiveSubscriptionStatus.value) ?? t('product__prepurchase--buyNowCta');
});

async function handleClick() {
    const { join: { subscription: { intro, confirm } } } = rentalRouteNames;
    const { resumeFromPause } = reviewRouteObjects;

    const routeMap = new Map([
        [SUBSCRIPTION_STATUSES.nonSubscriber, { name: intro }],
        [SUBSCRIPTION_STATUSES.paused, resumeFromPause],
        [SUBSCRIPTION_STATUSES.cancelled, { name: confirm }],
    ]);

    const routeObject = routeMap.get(effectiveSubscriptionStatus.value);

    if (routeObject) {
        router.push(routeObject);
        return;
    }

    await clickedPurchaseToBox();
}
</script>

<style lang="scss">
.c-pre-purchase-buy-button {
    button {
        width: 100%;
    }

    &__alert-icon {
        margin-right: $nu-spacer-1;
    }
}
</style>
